/**
 * **C05 · The budget book** (Stream C, Documents.dc.html): for the directors' budget meeting —
 * a one-page summary in front (the festival level, then each area's receipts, cost and margin),
 * then every line by area: its rule chip, last year's actual, the proposed figure, why. The
 * figures are Budget's (`GET /money/budget/:fy/festival` and `/rules`, every line from its rule);
 * this lays them out and computes only each area's sums and margin from the rows it is handed.
 * The meeting agenda the drawing names for the back is not drawn, so it is not built.
 */
import { shortDay } from './year_end_page.js';
import { NO_DATA, type PaperCell, type PaperPage } from './paper_page.js';

/** A festival-level line as Budget serves it. */
export interface BookFestivalLine {
  key: string;
  line: string;
  lastCents: number | null;
  thisCents: number | null;
}

export interface BookFestival {
  shared: BookFestivalLine[];
  offTop: BookFestivalLine[];
  totals: {
    receipts: { last: number; this: number };
    shared: { last: number; this: number };
    areaCosts: { last: number; this: number };
    offTop: { last: number | null; this: number | null };
    toDistribute: { last: number | null; this: number | null };
  };
}

/** A budget line as Budget's rules serve it. */
export interface BookLine {
  accountNumber: string;
  accountName: string;
  areaName: string | null;
  side: 'receipts' | 'cost';
  owner: string;
  years: { fiscalYear: number; cents: number | null }[];
  rule: string;
  proposedCents: number;
  reason: string;
}

export interface BudgetBookInput {
  /** The fiscal year budgeted (FY2028 is the 2027 festival's budget). */
  fiscalYear: number;
  /** The day the book is printed (YYYY-MM-DD, Detroit). */
  day: string;
  /** The directors' budget meeting (the finance year's `meeting` row); null when not set. */
  meeting: string | null;
  festival: BookFestival | null;
  lines: { receipts: BookLine[]; costs: BookLine[] } | null;
  /** Each area's chairs by name, from Roles. */
  chairs: Record<string, string[]>;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** `2027-02-24` → `Feb 24, 2027`. */
const dayYear = (ymd: string | null): string => {
  const m = ymd ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd) : null;
  return m ? `${MONTHS[Number(m[2]) - 1] ?? ''} ${String(Number(m[3]))}, ${m[1] ?? ''}` : NO_DATA;
};
const fig = (c: number | null | undefined): PaperCell => (typeof c === 'number' ? c : NO_DATA);
const last = (l: BookLine): number | null => l.years.at(-1)?.cents ?? null;
const SHARED = 'Shared';

export function budgetBook(i: BudgetBookInput): PaperPage[] {
  const festival = String(i.fiscalYear - 1);
  const before = String(i.fiscalYear - 2);
  const f = i.festival;
  const all = i.lines ? [...i.lines.receipts, ...i.lines.costs] : [];
  const areas = [...new Set(all.map((l) => l.areaName ?? SHARED))].sort((a, b) =>
    a === SHARED ? 1 : b === SHARED ? -1 : a.localeCompare(b),
  );
  const off = (key: string): BookFestivalLine | undefined => f?.offTop.find((x) => x.key === key);
  const pass = off('pass');
  const offWords = ['AOD', 'CRE', 'seed']
    .filter((w) => f?.offTop.some((x) => x.key === w.toLowerCase()))
    .join(' · ');

  const byArea = areas
    .filter((a) => a !== SHARED)
    .map((a) => {
      const mine = all.filter((l) => l.areaName === a);
      const sum = (side: BookLine['side']): number =>
        mine.filter((l) => l.side === side).reduce((n, l) => n + (last(l) ?? 0), 0);
      const receipts = sum('receipts');
      const cost = sum('cost');
      return [
        receipts ? a : `${a} · cost center`,
        receipts ? receipts : '—',
        cost,
        // A cost center's measure is per attendee-day; attendance is not in the books.
        receipts
          ? `${String(Math.round(((receipts - cost) / receipts) * 100))}%`
          : `${NO_DATA} per attendee-day`,
      ] as PaperCell[];
    });

  const pages = areas.map((a) => {
    const mine = all.filter((l) => (l.areaName ?? SHARED) === a);
    const owners = (side: BookLine['side'], what: string): string | null => {
      const o = [...new Set(mine.filter((l) => l.side === side).map((l) => l.owner))];
      return o.length ? `${o.join(' · ')} (${what})` : null;
    };
    const named = i.chairs[a] ?? [];
    const chairs = named.length ? `chair ${named.join(' · ')} asks` : null;
    return {
      title: `${a} · lines`,
      sub: `Owner: ${[owners('cost', 'cost'), owners('receipts', 'receipts'), chairs].filter(Boolean).join(' · ')}`,
      blocks: [
        {
          head: '',
          cols: '1fr 70px 90px 90px 1fr',
          rows: [
            ['LINE', 'RULE', before, festival, 'WHY'],
            ...mine.map((l) => [
              `${l.accountNumber} · ${l.accountName}`,
              l.rule,
              last(l) ?? '—',
              l.proposedCents,
              l.reason,
            ]),
          ],
        },
      ],
      foot: `Budget book · ${a}`,
    } satisfies PaperPage;
  });

  const summary: PaperPage = {
    title: `${festival} budget · summary`,
    sub: `For the directors’ budget meeting · ${dayYear(i.meeting)} · the baseline as of ${shortDay(i.day)}`,
    blocks: [
      {
        head: 'FESTIVAL LEVEL',
        cols: '1fr 120px 120px',
        rows: f
          ? [
              ['', `${before} ACTUAL`, `${festival} PROPOSED`],
              ['Receipts', f.totals.receipts.last, f.totals.receipts.this],
              [
                `Shared costs${f.shared.length ? ` · ${f.shared.map((x) => x.line.toLowerCase()).join(' · ')}` : ''}`,
                f.totals.shared.last,
                f.totals.shared.this,
              ],
              [
                `Obligations off the top${offWords ? ` · ${offWords}` : ''}`,
                fig(f.totals.offTop.last === null ? null : -f.totals.offTop.last),
                fig(f.totals.offTop.this === null ? null : -f.totals.offTop.this),
              ],
              ...(pass
                ? [
                    [
                      'School commitment fees · a pass-through, not an obligation',
                      pass.lastCents ?? '—',
                      pass.thisCents ?? '—',
                    ],
                  ]
                : []),
              ['Area cost lines', f.totals.areaCosts.last, f.totals.areaCosts.this],
              ['TO DISTRIBUTE', fig(f.totals.toDistribute.last), fig(f.totals.toDistribute.this)],
            ]
          : [[NO_DATA, '', '']],
      },
      {
        head: 'BY AREA · MARGIN',
        cols: '1fr 100px 100px 100px',
        rows: i.lines ? [['', 'RECEIPTS', 'COST', 'MARGIN'], ...byArea] : [[NO_DATA, '', '', '']],
      },
    ],
    foot: 'Budget book · summary',
  };

  const book = [summary, ...pages];
  const n = String(book.length);
  return book.map((p, k) => ({ ...p, foot: `${p.foot} · page ${String(k + 1)} of ${n}` }));
}
